import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const TYPE_COLORS = {
  SYSTEM: '#00d4ff',
  DONE: '#34d399',
  INFO: '#8b949e',
  WARN: '#fbbf24',
  ERROR: '#f87171',
};

/**
 * TaskConsole — bottom panel with timestamped log entries and task tracking
 *
 * Props:
 *   entries - [{ time, type, msg }]
 *   tasks - { running: number, completed: number, failed: number }
 *   taskList - [{ id, label, status, progress, message, cancel }] live tasks (lib/task-store.js); running ones get a progress bar and Cancel
 *   onClose()
 */
export default function TaskConsole({ entries = [], tasks = {}, taskList = [], onClose }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [activeTab, setActiveTab] = useState('log');
  const [filter, setFilter] = useState('all');

  const filteredEntries = filter === 'all'
    ? entries
    : entries.filter(e => e.type === filter.toUpperCase());

  return (
    <div style={{ background: C.bg1, borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', maxHeight: 200, flexShrink: 0 }}>
      {/* Header */}
      <div style={{ padding: '6px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['log', 'tasks'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4, border: 'none', background: activeTab === tab ? C.bg4 : 'transparent', color: activeTab === tab ? C.text0 : C.text3, cursor: 'pointer', textTransform: 'capitalize' }}>
              {tab}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {activeTab === 'log' && (
            <select value={filter} onChange={e => setFilter(e.target.value)}
              style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, outline: 'none' }}>
              <option value="all">All</option>
              <option value="system">System</option>
              <option value="done">Done</option>
              <option value="error">Error</option>
              <option value="warn">Warning</option>
            </select>
          )}
          <span style={{ fontSize: 10, color: C.text2 }}>{entries.length} entries</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 12 }}>✕</button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 14px', fontFamily: 'monospace' }}>
        {activeTab === 'log' && (
          filteredEntries.length === 0 ? (
            <div style={{ fontSize: 10, color: C.text2, padding: '10px 0', textAlign: 'center' }}>No log entries</div>
          ) : (
            filteredEntries.map((e, i) => (
              <div key={i} style={{ fontSize: 10, lineHeight: 1.6, display: 'flex', gap: 8 }}>
                <span style={{ color: C.text3, minWidth: 60 }}>{e.time}</span>
                <span style={{ color: TYPE_COLORS[e.type] || C.text2, fontWeight: 600, minWidth: 48 }}>{e.type}</span>
                <span style={{ color: C.text1 }}>{e.msg}</span>
              </div>
            ))
          )
        )}
        {activeTab === 'tasks' && (
          <div style={{ display: 'flex', gap: 20, padding: '12px 0' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.amber }}>{tasks.running || 0}</div>
              <div style={{ fontSize: 10, color: C.text2 }}>Running</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.emerald }}>{tasks.completed || 0}</div>
              <div style={{ fontSize: 10, color: C.text2 }}>Completed</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.red }}>{tasks.failed || 0}</div>
              <div style={{ fontSize: 10, color: C.text2 }}>Failed</div>
            </div>
          </div>
        )}
        {activeTab === 'tasks' && taskList.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 8 }}>
            {[...taskList].reverse().map(t => (
              <div key={t.id} style={{ fontSize: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, color: C.text1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span>
                  <span style={{ color: t.status === 'running' ? C.amber : t.status === 'done' ? C.emerald : t.status === 'failed' ? C.red : C.text3 }}>
                    {t.status === 'running' ? `${Math.round(t.progress * 100)}%${t.message ? ` · ${t.message}` : ''}` : t.status}
                  </span>
                  {t.status === 'running' && t.cancel && (
                    <button onClick={t.cancel} style={{ fontSize: 10, padding: '1px 8px', borderRadius: 4, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>Cancel</button>
                  )}
                </div>
                {t.status === 'running' && (
                  <div style={{ height: 3, borderRadius: 2, background: C.bg4, marginTop: 3 }}>
                    <div style={{ height: 3, borderRadius: 2, background: C.cyan, width: `${Math.round(t.progress * 100)}%`, transition: 'width .15s' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
